/* eslint-disable max-len */
import { GetCarMileageResponse } from '@1po/1po-bff-fe-spec/generated/catalog/maintenance_plan/response/GetCarMileageResponse';
import { GetPlannedOperationsResponse } from '@1po/1po-bff-fe-spec/generated/catalog/maintenance_plan/response/GetPlannedOperationsResponse';
import { GetIAMRepairMethodDetailResponse } from '@1po/1po-bff-fe-spec/generated/catalog/repair_methods/response/GetIAMRepairMethodDetailResponse';
import { GetIAMFamilyReferencesResponse } from '@1po/1po-bff-fe-spec/generated/catalog/service_operations/response/GetIAMFamilyReferencesResponse';
import { GetIAMServiceChecklistResponse } from '@1po/1po-bff-fe-spec/generated/catalog/service_operations/response/GetIAMServiceChecklistResponse';
import { GetIAMServiceOperationsResponse } from '@1po/1po-bff-fe-spec/generated/catalog/service_operations/response/GetIAMServiceOperationsResponse';
import { GetIAMServiceProposalOperationsResponse } from '@1po/1po-bff-fe-spec/generated/catalog/service_operations/response/GetIAMServiceProposalOperationsResponse';
import { GetIAMServiceProposalResponse } from '@1po/1po-bff-fe-spec/generated/catalog/service_operations/response/GetIAMServiceProposalResponse';

import {
  GET_IAM_OPERATIONS_FAMILIES_RESPONSE,
  GET_IAM_OPERATIONS_TREE_RESPONSE,
  GET_IAM_REPAIR_METHOD_DETAIL_RESPONSE,
  GET_IAM_SERVICE_CHECKLIST_RESPONSE,
  GET_IAM_SERVICE_PROPOSAL_OPERATIONS_RESPONSE,
  GET_IAM_SERVICE_PROPOSAL_RESPONSE,
  GET_MAINTENANCE_CAR_MILEAGE_RESPONSE,
  GET_MAINTENANCE_PLANNED_OPERATIONS_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import {
  createIAMSupplierReferenceKey,
  GET_IAM_SERVICE_OPERATIONS_REQUEST,
  parseIAMReferenceKeyNumber,
  parseIAMReferenceKeySupplier,
} from 'domains/catalog/Catalog.types';
import {
  ADD_OPERATION,
  ADD_REFERENCES,
  GET_IAM_REPAIR_METHOD_DETAIL_REQUEST,
  GET_IAM_SERVICE_CHECKLIST_REQUEST,
  GET_IAM_SERVICE_PROPOSAL_OPERATIONS_REQUEST,
  GET_IAM_SERVICE_PROPOSALS_REQUEST,
  GET_RD_CAR_MILEAGE_REQUEST,
  GET_RD_PLANNED_OPERATIONS,
  GetPlannedOperationsResponseLocal,
  MAINTENANCE_PLAN_NAMESPACE,
  MaintenancePlanState,
  REMOVE_OPERATION,
  REMOVE_REFERENCES,
  ServiceReferenceLocal,
  SET_OPERATIONS,
  SET_PROPOSAL_MILEAGE_INPUT,
  SET_PROPOSAL_OPTION_INPUT,
  SET_PROPOSAL_REGISTRATION_DATE_INPUT,
  SET_REFERENCES,
} from 'domains/maintenancePlan/MaintenancePlan.types';
import { FOUND, getData, hasData, NO_DATA, NOT_FOUND, SEARCH_STATUS, SearchData } from 'utils';

const initialState: MaintenancePlanState = {
  vehicleCode: undefined,
  vehicleKey: undefined,
  serviceOperationsFamiliesIAM: undefined,
  serviceOperationsIAM: undefined,
  serviceOperationsReferences: new Map<string, ServiceReferenceLocal[]>(),
  serviceProposalsIAM: undefined,
  serviceProposalsOperationsIAM: undefined,
  registrationDate: undefined,
  mileage: undefined,
  option: undefined,
  serviceChecklist: undefined,
  repairMethodDetails: new Map<string, SearchData<string> | undefined>(),
  vinToMileage: new Map<string, SearchData<GetCarMileageResponse> | undefined>(),
  operationsProposal: new Map<string, SearchData<GetPlannedOperationsResponse> | undefined>(),
};

// Saga actions
export const fetchIAMServiceOperationsRequestSaga = createAction(GET_IAM_SERVICE_OPERATIONS_REQUEST);
export const fetchIAMServiceOperationsResponseGaga = createAction(GET_IAM_OPERATIONS_TREE_RESPONSE);
export const fetchIAMServiceFamiliesResponseSaga = createAction(GET_IAM_OPERATIONS_FAMILIES_RESPONSE);
export const fetchIAMServiceProposalsRequestSaga = createAction<{
  registrationDate: string;
}>(GET_IAM_SERVICE_PROPOSALS_REQUEST);
export const fetchIAMServiceProposalsResponseSaga = createAction(GET_IAM_SERVICE_PROPOSAL_RESPONSE);
export const fetchIAMServiceProposalOperationsRequestSaga = createAction<{
  versionCode: string;
  proposalId: string;
  mileage: string;
  registrationDate: string;
}>(GET_IAM_SERVICE_PROPOSAL_OPERATIONS_REQUEST);
export const fetchIAMServiceProposalOperationsResponseSaga = createAction(GET_IAM_SERVICE_PROPOSAL_OPERATIONS_RESPONSE);
export const fetchIAMChecklistRequestSaga = createAction(GET_IAM_SERVICE_CHECKLIST_REQUEST);
export const fetchIAMChecklistResponseSaga = createAction(GET_IAM_SERVICE_CHECKLIST_RESPONSE);
export const selectOperationRequest = createAction<{
  operationId: string;
}>(ADD_OPERATION);
export const removeOperationRequest = createAction<{
  operationId: string;
}>(REMOVE_OPERATION);
export const setOperationsRequest = createAction<{
  operationsIds: string[];
}>(SET_OPERATIONS);
export const setReferencesRequest = createAction<{
  operationId: string;
  family: string;
  references: string[];
}>(SET_REFERENCES);
export const addReferencesRequest = createAction<{
  operationId: string;
  family: string;
  references: string[];
}>(ADD_REFERENCES);
export const removeReferencesRequest = createAction<{
  operationId: string;
  references: string[];
}>(REMOVE_REFERENCES);
export const setRegistrationDateInputRequest = createAction<{
  registrationDate: moment.Moment;
}>(SET_PROPOSAL_REGISTRATION_DATE_INPUT);
export const setMileageInputRequest = createAction<{
  mileage: number;
}>(SET_PROPOSAL_MILEAGE_INPUT);
export const setOptionInputRequest = createAction<{
  option: string | undefined;
}>(SET_PROPOSAL_OPTION_INPUT);

export const fetchIAMRepairMethodDetail = createAction<{
  categoryId: string;
}>(GET_IAM_REPAIR_METHOD_DETAIL_REQUEST);

export const fetchIAMRepairMethodDetailResponseSaga = createAction(GET_IAM_REPAIR_METHOD_DETAIL_RESPONSE);

//R/D maintenance plan
export const fetchCarMileageRequestSaga = createAction(GET_RD_CAR_MILEAGE_REQUEST);
export const fetchCarMileageResponseSaga = createAction(GET_MAINTENANCE_CAR_MILEAGE_RESPONSE);
export const fetchPlannedOperationsRequestSaga = createAction<{
  mileageKm: number;
}>(GET_RD_PLANNED_OPERATIONS);
export const fetchPlannedOperationsResponseSaga = createAction(GET_MAINTENANCE_PLANNED_OPERATIONS_RESPONSE);

// Slice
const slice = createSlice({
  name: MAINTENANCE_PLAN_NAMESPACE,
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setServiceOperationsStatus: (
      state,
      { payload }: PayloadAction<{ vehicleKey: string; versionCode: string | undefined; status: NO_DATA }>,
    ) => {
      const { vehicleKey, status } = payload;
      state.vehicleKey = vehicleKey;
      state.serviceOperationsIAM = status;
    },
    setServiceOperationsTree: (state, { payload }: PayloadAction<GetIAMServiceOperationsResponse>) => {
      state.serviceOperationsIAM = payload;
    },
    setFamilyReferences: (state, { payload }: PayloadAction<GetIAMFamilyReferencesResponse>) => {
      state.serviceOperationsFamiliesIAM = payload;
    },
    setServiceProposalsStatus: (
      state,
      { payload }: PayloadAction<{ vehicleKey: string | undefined; versionCode: string | undefined; status: NO_DATA }>,
    ) => {
      const { status } = payload;
      state.serviceProposalsIAM = status;
    },
    setServiceProposals: (state, { payload }: PayloadAction<GetIAMServiceProposalResponse>) => {
      state.serviceProposalsIAM = payload;
    },
    setServiceProposalOperationsStatus: (
      state,
      { payload }: PayloadAction<{ vehicleKey: string; versionCode: string | undefined; status: NO_DATA }>,
    ) => {
      const { status } = payload;
      state.serviceProposalsOperationsIAM = status;
    },
    setServiceProposalOperations: (state, { payload }: PayloadAction<GetIAMServiceProposalOperationsResponse>) => {
      state.serviceProposalsOperationsIAM = payload;
    },
    selectOperation: (state, action: PayloadAction<{ operationId: string }>) => {
      if (state.serviceOperationsIAM && hasData(state.serviceOperationsIAM)) {
        state.serviceOperationsIAM.mainOperations?.forEach((category) => {
          category.operations.forEach((operation) => {
            if (operation.id === action.payload.operationId) {
              operation.selected = true;
            }
          });
        });
        state.serviceOperationsIAM.additionalOperations?.forEach((category) => {
          category.operations.forEach((operation) => {
            if (operation.id === action.payload.operationId) {
              operation.selected = true;
            }
          });
        });
      }
    },
    unselectOperation: (state, action: PayloadAction<{ operationId: string }>) => {
      if (state.serviceOperationsIAM && hasData(state.serviceOperationsIAM)) {
        state.serviceOperationsIAM.mainOperations?.forEach((category) => {
          category.operations.forEach((operation) => {
            if (operation.id === action.payload.operationId) {
              operation.selected = false;
            }
          });
        });
        state.serviceOperationsIAM.additionalOperations?.forEach((category) => {
          category.operations.forEach((operation) => {
            if (operation.id === action.payload.operationId) {
              operation.selected = false;
            }
          });
        });
      }
    },
    setSelectedOperations: (state, action: PayloadAction<{ operationsIds: string[] }>) => {
      if (state.serviceOperationsIAM && hasData(state.serviceOperationsIAM)) {
        state.serviceOperationsIAM.mainOperations?.forEach((category) => {
          category.operations.forEach((operation) => {
            operation.selected = action.payload.operationsIds.includes(operation.id);
          });
        });
        state.serviceOperationsIAM.additionalOperations?.forEach((category) => {
          category.operations.forEach((operation) => {
            operation.selected = action.payload.operationsIds.includes(operation.id);
          });
        });
      }
    },
    addSelectedReferences: (
      state,
      action: PayloadAction<{
        operationId: string;
        family: string;
        references: string[];
      }>,
    ) => {
      const existingReferences = state.serviceOperationsReferences.get(action.payload.operationId) ?? [];
      const toBeAdded = action.payload.references
        .filter(
          (item) =>
            !existingReferences.some(
              (ref) => createIAMSupplierReferenceKey(ref.referenceNumber, ref.supplierCode) === item,
            ),
        )
        .map((referenceKey) => {
          return {
            quantity: 1,
            referenceNumber: parseIAMReferenceKeyNumber(referenceKey),
            supplierCode: parseIAMReferenceKeySupplier(referenceKey),
            family: action.payload.family,
          };
        });
      const newReferences = [...existingReferences, ...toBeAdded];
      state.serviceOperationsReferences.set(action.payload.operationId, newReferences);
    },
    removeSelectedReferences: (
      state,
      action: PayloadAction<{
        operationId: string;
        references: string[];
      }>,
    ) => {
      const existingReferences = state.serviceOperationsReferences.get(action.payload.operationId);
      const newReferences =
        existingReferences?.filter(
          (r) => !action.payload.references.includes(createIAMSupplierReferenceKey(r.referenceNumber, r.supplierCode)),
        ) ?? [];
      state.serviceOperationsReferences.set(action.payload.operationId, newReferences);
    },
    setSelectedReferences: (
      state,
      action: PayloadAction<{
        operationId: string;
        family: string;
        references: string[];
      }>,
    ) => {
      const existingReferences = state.serviceOperationsReferences.get(action.payload.operationId);
      const newReferences = action.payload.references.map(
        (r) =>
          existingReferences?.find(
            (existing) => createIAMSupplierReferenceKey(existing.referenceNumber, existing.supplierCode) === r,
          ) ?? {
            referenceNumber: parseIAMReferenceKeyNumber(r),
            supplierCode: parseIAMReferenceKeySupplier(r),
            quantity: 1,
            family: action.payload.family,
          },
      );
      state.serviceOperationsReferences.set(action.payload.operationId, newReferences);
    },
    setSelectedReferenceQuantity: (
      state,
      action: PayloadAction<{ operationId: string; referenceKey: string; newQuantity: number }>,
    ) => {
      const existingReferences = state.serviceOperationsReferences.get(action.payload.operationId);

      const newReferences =
        existingReferences?.map((ref) =>
          createIAMSupplierReferenceKey(ref.referenceNumber, ref.supplierCode) === action.payload.referenceKey
            ? {
                ...ref,
                quantity: action.payload.newQuantity,
              }
            : ref,
        ) ?? ([] as ServiceReferenceLocal[]);
      state.serviceOperationsReferences.set(action.payload.operationId, newReferences);
    },
    setRegistrationDateInput: (state, action: PayloadAction<{ registrationDate: moment.Moment }>) => {
      state.registrationDate = action.payload.registrationDate;
    },
    setMileageInput: (state, action: PayloadAction<{ mileage: number }>) => {
      state.mileage = action.payload.mileage;
    },
    setOptionInput: (state, action: PayloadAction<{ option: string | undefined }>) => {
      state.option = action.payload.option;
    },

    setServiceChecklistStatus: (state, { payload }: PayloadAction<{ status: NO_DATA }>) => {
      state.serviceChecklist = {
        searchStatus: payload.status,
      };
    },
    resetServiceChecklist: (state) => {
      state.serviceChecklist = undefined;
    },
    setServiceChecklist: (state, { payload }: PayloadAction<GetIAMServiceChecklistResponse>) => {
      state.serviceChecklist = {
        searchStatus: payload.checklist ? FOUND : NOT_FOUND,
        data: payload,
      };
    },
    setRepairMethodDetails: (state, { payload }: PayloadAction<GetIAMRepairMethodDetailResponse>) => {
      const result: SearchData<string> = {
        searchStatus: payload.repairMethodDetail ? FOUND : NOT_FOUND,
        data: payload.repairMethodDetail,
      };
      state.repairMethodDetails.set(payload.categoryId, result);
    },
    setRepairMethodDetailsStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        categoryId: string;
        status: SEARCH_STATUS;
      }>,
    ) => {
      const result: SearchData<string> = {
        searchStatus: payload.status,
      };
      state.repairMethodDetails.set(payload.categoryId, result);
    },
    setCarMileageStatus: (state, { payload }: PayloadAction<{ vin: string; status: NO_DATA }>) => {
      const result: SearchData<GetCarMileageResponse> = {
        searchStatus: payload.status,
      };
      state.vinToMileage.set(payload.vin, result);
    },
    setCarMileage: (state, { payload }: PayloadAction<{ mileage: GetCarMileageResponse }>) => {
      const result: SearchData<GetCarMileageResponse> = {
        searchStatus: payload.mileage ? FOUND : NOT_FOUND,
        data: payload.mileage,
      };
      state.vinToMileage.set(payload.mileage.vin, result);
    },

    setPlannedOperationsStatus: (state, { payload }: PayloadAction<{ vin: string; status: NO_DATA }>) => {
      const data = state.operationsProposal.get(payload.vin)?.data;
      const result: SearchData<GetPlannedOperationsResponseLocal> = {
        searchStatus: payload.status,
        data,
      };
      state.operationsProposal.set(payload.vin, result);
    },
    setPlannedOperations: (state, { payload }: PayloadAction<{ operations: GetPlannedOperationsResponse }>) => {
      const mileage = state.operationsProposal.get(payload.operations.vin)?.data?.baseMileage;
      const result: SearchData<GetPlannedOperationsResponseLocal> = {
        searchStatus: payload.operations ? FOUND : NOT_FOUND,
        data: {
          ...payload.operations,
          baseMileage:
            mileage ||
            (payload.operations.operations && payload.operations.operations.length > 0
              ? payload.operations.operations[0].usedVehicleMileage
              : undefined),
        },
      };
      state.operationsProposal.set(payload.operations.vin, result);
    },
  },
});

// Actions
export const {
  setInitialState,
  selectOperation,
  unselectOperation,
  setSelectedReferences,
  setFamilyReferences,
  setServiceOperationsStatus,
  setServiceOperationsTree,
  setSelectedReferenceQuantity,
  addSelectedReferences,
  removeSelectedReferences,
  setServiceProposalOperations,
  setServiceProposals,
  setServiceProposalsStatus,
  setServiceProposalOperationsStatus,
  setSelectedOperations,
  setRegistrationDateInput,
  setOptionInput,
  setMileageInput,

  setServiceChecklistStatus,
  setServiceChecklist,
  resetServiceChecklist,
  setRepairMethodDetails,
  setRepairMethodDetailsStatus,
  setCarMileage,
  setCarMileageStatus,
  setPlannedOperations,
  setPlannedOperationsStatus,
} = slice.actions;

export const getLastSearchServiceFamilies = createSelector(
  (state: RootState) => state.maintenancePlan.serviceOperationsFamiliesIAM,
  (serviceOperationsFamiliesIAM) => serviceOperationsFamiliesIAM,
);

export const getServiceOperationsReferences = createSelector(
  (state: RootState) => state.maintenancePlan.serviceOperationsReferences,
  (serviceOperationsReferences) => serviceOperationsReferences,
);

export const getLastSearchServiceOperationsData = createSelector(
  (state: RootState) => state.maintenancePlan.serviceOperationsIAM,
  (serviceOperationsIAM) => getData(serviceOperationsIAM),
);

export const getLastSearchServiceOperations = createSelector(
  (state: RootState) => state.maintenancePlan.serviceOperationsIAM,
  (serviceOperationsIAM) => serviceOperationsIAM,
);

export const getServiceProposal = createSelector(
  (state: RootState) => state.maintenancePlan.serviceProposalsIAM,
  (serviceProposals) => serviceProposals,
);

export const getServiceProposalOperations = createSelector(
  (state: RootState) => state.maintenancePlan.serviceProposalsOperationsIAM,
  (proposal) => proposal,
);

export const getRegistrationDateInput = createSelector(
  (state: RootState) => state.maintenancePlan.registrationDate,
  (registrationDate) => registrationDate,
);

export const getMileageInput = createSelector(
  (state: RootState) => state.maintenancePlan.mileage,
  (mileage) => mileage,
);

export const getOptionInput = createSelector(
  (state: RootState) => state.maintenancePlan.option,
  (option) => option,
);

export const getIAMServiceChecklist = createSelector(
  (state: RootState) => state.maintenancePlan.serviceChecklist,
  (serviceChecklist) => serviceChecklist,
);

export const getIAMServiceChecklistStatus = createSelector(
  (state: RootState) => state.maintenancePlan.serviceChecklist?.searchStatus,
  (serviceChecklistStatus) => serviceChecklistStatus,
);

export const getIAMRepairMethodDetailStatus = createSelector(
  (state: RootState) => state.maintenancePlan.repairMethodDetails,
  (_state: RootState, categoryId: string | undefined) => categoryId,
  (repairMethodDetail, categoryId) => {
    if (categoryId) {
      return repairMethodDetail.get(categoryId)?.searchStatus;
    }
  },
);

export const getIAMRepairMethodDetailData = createSelector(
  (state: RootState) => state.maintenancePlan.repairMethodDetails,
  (_state: RootState, categoryId: string | undefined) => categoryId,
  (repairMethodDetail, categoryId) => {
    if (categoryId) {
      return repairMethodDetail.get(categoryId)?.data;
    }
    return undefined;
  },
);

export const getIAMRepairMethodDetail = createSelector(
  (state: RootState) => state.maintenancePlan.repairMethodDetails,
  (_state: RootState, categoryId: string | undefined) => categoryId,
  (repairMethodDetail, categoryId) => {
    if (categoryId) {
      return repairMethodDetail.get(categoryId);
    }
    return undefined;
  },
);

//R/D/A brands
export const getCarMileage = createSelector(
  (state: RootState) => state.maintenancePlan.vinToMileage,
  (_state: RootState, vin: string | undefined) => vin,
  (vinToMileage, vin) => {
    if (vin) {
      return vinToMileage.get(vin);
    }
    return undefined;
  },
);

export const getPlannedOperations = createSelector(
  (state: RootState) => state.maintenancePlan.operationsProposal,
  (_state: RootState, vin: string | undefined) => vin,
  (operations, vin) => {
    if (vin) {
      return operations.get(vin);
    }
    return undefined;
  },
);

// Export reducer
export default slice.reducer;
